import React from "react";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import axios from "axios";
import { toast } from "react-toastify";
import { FiArrowLeft, FiCheckCircle, FiMinus, FiPlus } from "react-icons/fi";
import { Field, PrimaryButton, SelectField } from "../../../components/landing/ui";
import { ValidationSchema } from "./buy-meter-validation";
import { API_URL } from "../../../config/env";

const METER_TYPES = [
  "DISCOs (EKEDC, IKEDC, IBEDC E.T.C)",
  "PRIVATE ENERGY SMART METERS",
  "PRIVATE WATER SMART METERS",
];

const PHASES = [
  { value: "prepaid", label: "Single phase", hint: "Homes and small shops" },
  { value: "postpaid", label: "Three phase", hint: "Larger loads" },
];

const initialValues = {
  firstName: "",
  lastName: "",
  phoneNumber: "",
  email: "",
  type: "",
  phase: "prepaid",
  quantity: 1,
};

export default function BuyMeterWrapper() {
  const [isSuccess, setIsSuccess] = React.useState(false);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const formik = useFormik({
    initialValues,
    validationSchema: ValidationSchema,
    validateOnBlur: true,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        const res = await axios.post(`${API_URL}/metering/buy-meter`, values);
        toast.success(res.data?.message || "Request received");
        resetForm();
        setIsSuccess(true);
      } catch (error) {
        toast.error(error.response?.data?.message || "An error occurred");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { values, errors, touched, handleChange, handleBlur, setFieldValue } = formik;
  const err = (k) => (touched[k] && errors[k]) || "";
  const bind = (k) => ({ name: k, value: values[k], onChange: handleChange, onBlur: handleBlur, error: err(k) });
  const busy = formik.isSubmitting;

  if (isSuccess) {
    return (
      <section className="mx-auto flex w-[92%] max-w-[520px] flex-col items-center gap-5 py-24 text-center">
        <span className="flex h-20 w-20 items-center justify-center rounded-full bg-green-100 text-4xl text-green-600">
          <FiCheckCircle />
        </span>
        <h1 className="text-3xl font-extrabold tracking-tight text-primary-dark">Request received</h1>
        <p className="text-lg text-[#5b5f73]">Thanks! Our team will reach out to you shortly about your meter.</p>
        <Link
          to="/"
          className="mt-2 rounded-full bg-primary-dark px-7 py-3.5 font-semibold text-white transition hover:bg-primary-light"
        >
          Back to home
        </Link>
      </section>
    );
  }

  return (
    <section className="mx-auto w-[92%] max-w-[640px] py-10 lg:py-16">
      <Link
        to="/"
        className="mb-6 inline-flex items-center gap-2 rounded-full bg-[#F3F3F7] px-4 py-2 text-sm font-semibold text-primary-dark transition hover:bg-[#E6E6EC]"
      >
        <FiArrowLeft />
        Back to home
      </Link>
      <div className="mb-8 space-y-3 text-center">
        <span className="inline-flex rounded-full bg-secondary-dark/15 px-3.5 py-1 text-[13px] font-semibold text-[#9a6f08]">
          Get a meter
        </span>
        <h1 className="text-3xl font-extrabold tracking-tight text-primary-dark sm:text-4xl">Request a meter</h1>
        <p className="text-[#5b5f73]">Tell us what you need and we'll get in touch.</p>
      </div>

      <form
        onSubmit={formik.handleSubmit}
        noValidate
        className="flex flex-col gap-5 rounded-3xl border border-black/5 bg-white p-5 shadow-[0_20px_50px_-20px_rgba(1,6,35,0.2)] sm:p-8"
      >
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="First name" placeholder="First name" autoComplete="given-name" disabled={busy} {...bind("firstName")} />
          <Field label="Last name" placeholder="Last name" autoComplete="family-name" disabled={busy} {...bind("lastName")} />
          <Field label="Phone number" placeholder="08012345678" inputMode="numeric" maxLength={11} autoComplete="tel" disabled={busy} {...bind("phoneNumber")} />
          <Field label="Email" type="email" placeholder="you@example.com" autoComplete="email" disabled={busy} {...bind("email")} />
        </div>

        <SelectField label="Meter type" disabled={busy} {...bind("type")}>
          <option value="" disabled>
            Select a meter type
          </option>
          {METER_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </SelectField>

        <fieldset className="flex flex-col gap-1.5">
          <legend className="mb-1.5 text-sm font-semibold text-primary-dark">Phase</legend>
          <div className="grid grid-cols-2 gap-3">
            {PHASES.map((p) => {
              const active = values.phase === p.value;
              return (
                <button
                  key={p.value}
                  type="button"
                  disabled={busy}
                  onClick={() => setFieldValue("phase", p.value)}
                  aria-pressed={active}
                  className={`rounded-xl border-2 px-4 py-3 text-left transition ${
                    active ? "border-primary-dark bg-primary-dark/5" : "border-transparent bg-[#F3F3F7] hover:bg-[#ECECF2]"
                  }`}
                >
                  <span className="block font-semibold text-primary-dark">{p.label}</span>
                  <span className="block text-xs text-[#6C6F7F]">{p.hint}</span>
                </button>
              );
            })}
          </div>
        </fieldset>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold text-primary-dark">Quantity</label>
          <div className="flex items-center gap-3">
            <button
              type="button"
              aria-label="Decrease quantity"
              disabled={busy || Number(values.quantity) <= 1}
              onClick={() => setFieldValue("quantity", Math.max(1, Number(values.quantity) - 1))}
              className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#F3F3F7] text-primary-dark transition hover:bg-[#E6E6EC] disabled:opacity-40"
            >
              <FiMinus />
            </button>
            <input
              name="quantity"
              type="number"
              inputMode="numeric"
              min={1}
              value={values.quantity}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={busy}
              className="h-12 w-24 rounded-xl bg-[#F3F3F7] text-center text-lg font-bold outline-none"
            />
            <button
              type="button"
              aria-label="Increase quantity"
              disabled={busy}
              onClick={() => setFieldValue("quantity", Number(values.quantity || 0) + 1)}
              className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#F3F3F7] text-primary-dark transition hover:bg-[#E6E6EC]"
            >
              <FiPlus />
            </button>
          </div>
          {err("quantity") && <span className="text-sm font-medium text-red-600">{err("quantity")}</span>}
        </div>

        <PrimaryButton type="submit" loading={busy} disabled={busy}>
          Request meter
        </PrimaryButton>
      </form>
    </section>
  );
}
